import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Field, Input, Textarea } from '../components/ui/Field';
import { getSettings, updateSettings, type PlatformSettings } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatBytes, formatDate } from '../lib/utils';

export function SettingsPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ['settings'], queryFn: () => getSettings(token!) });
  const [form, setForm] = useState<PlatformSettings | null>(null);

  useEffect(() => {
    if (settingsQuery.data?.settings) {
      setForm(settingsQuery.data.settings);
    }
  }, [settingsQuery.data?.settings]);

  const saveMutation = useMutation({
    mutationFn: (settings: PlatformSettings) => updateSettings(token!, settings),
    onSuccess: async (result) => {
      setForm(result.settings);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    }
  });

  if (!form) {
    return <p className="text-sm text-ink/70">Loading settings</p>;
  }

  const galleryEmailPreview = previewGalleryEmail(form.galleryShareEmailHtml);

  return (
    <form
      className="grid gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        saveMutation.mutate(form);
      }}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">Settings</h1>
          <p className="mt-1 text-sm text-ink/70">Last updated {formatDate(form.updatedAt)}{form.updatedBy ? ` by ${form.updatedBy}` : ''}</p>
        </div>
        <Button type="submit" disabled={saveMutation.isPending}>
          <Save className="h-4 w-4" />
          <span>Save</span>
        </Button>
      </div>

      <section className="grid gap-4 rounded-sm bg-white p-5 shadow-sm ring-1 ring-ink/10 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <h2 className="font-semibold">Platform limits</h2>
        </div>
        <Field label="Max upload size" hint={formatBytes(form.maxUploadBytes)}>
          <Input
            type="number"
            min={1}
            value={Math.round(form.maxUploadBytes / (1024 * 1024))}
            onChange={(event) => setForm({ ...form, maxUploadBytes: Number(event.target.value) * 1024 * 1024 })}
          />
        </Field>
        <Field label="Max photos per gallery">
          <Input type="number" min={1} value={form.maxPhotosPerGallery} onChange={(event) => setForm({ ...form, maxPhotosPerGallery: Number(event.target.value) })} />
        </Field>
        <Field label="Max video upload size" hint={formatBytes(form.maxVideoUploadBytes)}>
          <Input
            type="number"
            min={1}
            max={5120}
            value={Math.round(form.maxVideoUploadBytes / (1024 * 1024))}
            onChange={(event) => setForm({ ...form, maxVideoUploadBytes: Number(event.target.value) * 1024 * 1024 })}
          />
        </Field>
        <Field label="Max video duration (minutes)">
          <Input
            type="number"
            min={1 / 60}
            max={240}
            step={1 / 60}
            value={form.maxVideoDurationSeconds / 60}
            onChange={(event) => setForm({ ...form, maxVideoDurationSeconds: Math.round(Number(event.target.value) * 60) })}
          />
        </Field>
        <Field label="Max videos per gallery">
          <Input
            type="number"
            min={0}
            max={100}
            value={form.maxVideosPerGallery}
            onChange={(event) => setForm({ ...form, maxVideosPerGallery: Number(event.target.value) })}
          />
        </Field>
        <Field label="Watermark renders per gallery per day" hint="Resets at midnight in South Africa.">
          <Input
            type="number"
            min={1}
            max={20}
            value={form.maxWatermarkRendersPerGalleryPerDay}
            onChange={(event) => setForm({ ...form, maxWatermarkRendersPerGalleryPerDay: Number(event.target.value) })}
          />
        </Field>
        <Field label="Signed media URL TTL">
          <Input
            type="number"
            min={60}
            value={form.signedMediaUrlTtlSeconds}
            onChange={(event) => setForm({ ...form, signedMediaUrlTtlSeconds: Number(event.target.value) })}
          />
        </Field>
        <Field label="Export ZIP retention days">
          <Input
            type="number"
            min={1}
            value={form.exportZipRetentionDays}
            onChange={(event) => setForm({ ...form, exportZipRetentionDays: Number(event.target.value) })}
          />
        </Field>
      </section>

      <section className="grid gap-4 rounded-sm bg-white p-5 shadow-sm ring-1 ring-ink/10 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <h2 className="font-semibold">Gallery sharing email</h2>
          <p className="mt-1 text-sm text-ink/70">
            Sent when a photographer shares a gallery by email. Supported placeholders: {'{{galleryTitle}}'}, {'{{galleryUrl}}'}, {'{{passcode}}'}, and {'{{photographerName}}'}.
          </p>
        </div>
        <div className="lg:col-span-2">
          <Field label="Email subject">
            <Input
              value={form.galleryShareEmailSubject}
              maxLength={200}
              onChange={(event) => setForm({ ...form, galleryShareEmailSubject: event.target.value })}
            />
          </Field>
        </div>
        <div className="lg:col-span-2">
          <Field label="HTML template" hint="Supported placeholders: {{galleryTitle}}, {{galleryUrl}}, {{passcode}}, {{photographerName}}, {{customMessage}}. Scripts are disabled in the preview.">
            <Textarea
              className="min-h-[30rem] font-mono text-xs leading-relaxed"
              value={form.galleryShareEmailHtml}
              spellCheck={false}
              onChange={(event) => setForm({ ...form, galleryShareEmailHtml: event.target.value })}
            />
          </Field>
        </div>
        <div className="lg:col-span-2">
          <p className="mb-2 text-sm font-medium text-ink">Preview</p>
          <iframe
            title="Gallery sharing email preview"
            srcDoc={galleryEmailPreview}
            sandbox=""
            className="h-[720px] w-full rounded-sm border border-ink/10 bg-white"
          />
        </div>
      </section>

      <section className="grid gap-4 rounded-sm bg-white p-5 shadow-sm ring-1 ring-ink/10 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <h2 className="font-semibold">Invoice defaults</h2>
        </div>
        <Field label="Currency">
          <Input value={form.invoiceCurrency} maxLength={3} onChange={(event) => setForm({ ...form, invoiceCurrency: event.target.value.toUpperCase() })} />
        </Field>
        <Field label="Sender email">
          <Input value={form.invoiceSenderEmail} onChange={(event) => setForm({ ...form, invoiceSenderEmail: event.target.value })} />
        </Field>
        <Field label="Sender name">
          <Input value={form.invoiceSenderName} onChange={(event) => setForm({ ...form, invoiceSenderName: event.target.value })} />
        </Field>
        <Field label="Payment terms">
          <Input value={form.invoicePaymentTerms} onChange={(event) => setForm({ ...form, invoicePaymentTerms: event.target.value })} />
        </Field>
        <div className="lg:col-span-2">
          <Field label="Sender address">
            <Textarea value={form.invoiceSenderAddress} onChange={(event) => setForm({ ...form, invoiceSenderAddress: event.target.value })} />
          </Field>
        </div>
      </section>

      {saveMutation.error ? <p className="text-sm text-clay">{saveMutation.error.message}</p> : null}
    </form>
  );
}

function previewGalleryEmail(template: string): string {
  return template
    .replaceAll('{{galleryTitle}}', 'Ryan &amp; Julia')
    .replaceAll('{{galleryUrl}}', 'https://storyly.yourl.co.za/g/die-humans-photography/ryan-julia')
    .replaceAll('{{passcode}}', 'garden-2026')
    .replaceAll('{{photographerName}}', 'Die Humans Photography');
}
